import {
  Table as BaseTable,
  TableBody,
  TableContainer,
  TableHead,
  TableSortLabel,
  TableRow,
  withStyles,
} from '@material-ui/core';
import moment from 'moment';
import React from 'react';

import { EeInput } from 'components/EeInput';
import { Section } from 'components/Section';
import { StickyTableCell } from 'components/table/StickyTableCell';
import { TableCell } from 'components/table/TableCell';
import { Tooltip } from 'components/Tooltip';

import { Cell, Column } from './types';
import { useTable } from './useTable';


const StyledTableSortLabel = withStyles(() => ({
  active: {color: '#385f99!important'},
}))(TableSortLabel);

export function Table(props: {
  sectionTitle: string,
  isFirstColumnSticky?: boolean,
  columns: Column[],
  rows: (Cell|undefined)[][],
  noSearch?: boolean,
}) {
  const logic = useTable();

  return (
    <Section title={props.sectionTitle}>
      {!props.noSearch &&
        <EeInput
          value={logic.search.value}
          search
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => logic.search.setValue(e.target.value)}
          type="search"
          className="mb-3"
          notFullWidth
          placeholder="Search items"
          style={{border: '1px solid #3B3B3B', borderRadius: 0, width: 450}}
        />
      }
      <TableContainer>
        <BaseTable stickyHeader aria-label='sticky table'>
          <TableHead>
            <TableRow>
              {props.isFirstColumnSticky
                ? <StickyTableCell inHead key={0} {...props.columns[0].cellProps}>
                  {props.columns[0].nonSortable
                    ? props.columns[0].label
                    : <StyledTableSortLabel
                        active={logic.sortByCol === 0}
                        direction={logic.sortByCol === 0 ? logic.sortDirection : undefined}
                        onClick={() => logic.handleSort(0)}
                      >
                        {props.columns[0].label}
                      </StyledTableSortLabel>
                  }
                  {props.columns[0].tooltip && <Tooltip title={props.columns[0].tooltip}/>}
                  </StickyTableCell>
                : <TableCell inHead key={0} {...props.columns[0].cellProps}>
                    {props.columns[0].nonSortable
                      ? props.columns[0].label
                      : <StyledTableSortLabel
                          active={logic.sortByCol === 0}
                          direction={logic.sortByCol === 0 ? logic.sortDirection : undefined}
                          onClick={() => logic.handleSort(0)}
                        >
                          {props.columns[0].label}
                        </StyledTableSortLabel>
                    }
                    {props.columns[0].tooltip && <Tooltip title={props.columns[0].tooltip}/>}
                  </TableCell>
              }
              {props.columns.slice(1).map((c, i) => (
                <TableCell key={i+1} inHead {...c.cellProps}>
                  {c.nonSortable
                    ? c.label
                    : <StyledTableSortLabel
                        active={logic.sortByCol === i+1}
                        direction={logic.sortByCol === i+1 ? logic.sortDirection : undefined}
                        onClick={() => logic.handleSort(i+1)}
                      >
                        {c.label}
                      </StyledTableSortLabel>
                  }
                  {c.tooltip && <Tooltip title={c.tooltip}/>}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {(
              logic.search.value
                ? props.rows
                  .filter(
                    r => r.some(c => {
                      const value = c?.sortingValue === undefined ? c?.content : c?.sortingValue;
                      return typeof value === 'string'
                        ? String(value).toLowerCase().includes(logic.search.value.toLowerCase())
                        : false;
                      })
                  )
                : props.rows
              )
              .sort((a, b) => {
                if (logic.sortByCol === undefined) return 0;
                const aValue = a[logic.sortByCol]?.sortingValue;
                const bValue = b[logic.sortByCol]?.sortingValue;
                let res: number;

                if (aValue === undefined || bValue === undefined) return 0;

                if (typeof aValue === 'string' && typeof bValue === 'string') {
                  res = ('' + aValue).localeCompare('' + bValue)
                } else if (
                  (typeof aValue === 'number' && typeof bValue === 'number')
                  || (typeof aValue === 'boolean' && typeof bValue === 'boolean')
                ) {
                  res = aValue - bValue;
                } else if (moment.isMoment(aValue) && moment.isMoment(bValue)) {
                  res = aValue.diff(bValue);
                } else {
                  console.warn(`Can't compare ${typeof aValue} and ${typeof bValue}`);
                  console.log('Values compared:', aValue, bValue);
                  return 0;
                }
                if (logic.sortDirection === 'desc') res *= -1;
                return res;
              })
              .map((r, i) => (
                <TableRow key={i}>
                  {props.isFirstColumnSticky
                    ? !r[0]
                      ? <StickyTableCell key={0}/>
                      : <StickyTableCell key={0} {...r[0].cellProps}>{r[0].content}</StickyTableCell>
                    : !r[0]
                      ? <TableCell key={0}/>
                      : <TableCell key={0} {...r[0].cellProps}>{r[0].content}</TableCell>
                  }
                  {r.slice(1).map((c, j) => (
                    !c
                      ? <TableCell key={j}/>
                      : <TableCell key={j} {...{align: 'left', ...c.cellProps}} >{c.content}</TableCell>
                  ))}
                </TableRow>
            ))}
          </TableBody>
        </BaseTable>
      </TableContainer>
    </Section>
  );
}
